/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import { v4 as uuid } from "uuid";
import Transformer from "./Transformer";

import { stripTags, addTags } from "./helpers";
import {
  ConditionObjectData,
  DroppedConditionData,
  ConditionError,
  TransformBuilderProps,
  TransformObjectData,
} from "@advapacs/common";
import Validator from "./validator";

const TransformBuilder = forwardRef((props: TransformBuilderProps, ref) => {
  const {
    initialValue,
    disabled,
    ruleTypes,
    title,
    onDropOut,
    transformType,
    hideCollapse,
    noResult,
    addBtnText,
    titleConfirmRemove,
    isDynamicConditionType = false,
    onHandleScriptModal,
    isDark = false,
  }: TransformBuilderProps = props;
  const [root, setRoot] = useState<TransformObjectData[]>([]);

  const getInitialValue = (init: any): TransformObjectData[] => {
    if (init) {
      const initialValueCopy: TransformObjectData[] = JSON.parse(
        JSON.stringify(init)
      );
      addTags(initialValueCopy);
      return initialValueCopy;
    }

    return [];
  };
  useEffect(() => {
    setRoot(getInitialValue(initialValue));
  }, [initialValue]);
  const [errors, setErrors] = useState<ConditionError[]>([]);

  const createTransform = () => {
    const condition = {
      type: "",
      id: uuid(),
    };

    const parent = JSON.parse(JSON.stringify(root));

    if (!parent) return;
    parent.push(condition as ConditionObjectData);
    setRoot(parent);
  };

  const onRemoveTransform = (id: string) => {
    const rootCopy = JSON.parse(JSON.stringify(root));
    const parent = rootCopy;
    if (!parent) return;
    const index = parent.findIndex((x: ConditionObjectData) => x.id === id);
    if (index !== -1) {
      parent.splice(index, 1);
      setRoot(parent);
    }
  };

  const onValueChangeHandler = (
    value: TransformObjectData,
    parentId?: string
  ) => {
    if (parentId) {
      const parent = JSON.parse(JSON.stringify(root));
      // update specific condition;
      const index = parent.findIndex(
        (x: ConditionObjectData) => x.id === parentId
      );
      parent.splice(index, 1, value as ConditionObjectData);
      setRoot(parent);
    } else {
      const parent = JSON.parse(JSON.stringify(root));
      // update specific condition;
      const index = parent.findIndex(
        (x: ConditionObjectData) => x.id === value.id
      );
      parent.splice(index, 1, value as ConditionObjectData);
      setRoot(parent);
    }
  };

  const onDropTransform = (condition: string, targetIndex: number) => {
    const draggedObject: DroppedConditionData = JSON.parse(condition);

    const { value } = draggedObject;
    let parent = JSON.parse(JSON.stringify(root));

    const newRule = { ...value, id: uuid() };

    // Add new item in new parent;
    parent.splice(targetIndex, 0, newRule);

    // Check if item is from here. We can drop into other rule builder components
    const index = parent.findIndex(
      (x: ConditionObjectData) => x.id === value.id
    );

    // Remove item from old parent
    parent = parent.filter((x: ConditionObjectData) => x.id !== value.id);
    setRoot(parent);

    if (index === -1) {
      // Dropped item is not from here. Tell caller to remove it from wherever it came from
      if (onDropOut) onDropOut(value.id);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      validate() {
        const parent = JSON.parse(JSON.stringify(root));
        const newErrors = new Validator().validate(parent, transformType);
        setErrors(newErrors);
        stripTags(parent);
        return {
          rule: parent,
          errors: newErrors,
        };
      },
      remove(id: string) {
        onRemoveTransform(id);
      },
      getData() {
        return JSON.parse(JSON.stringify(root));
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [root, transformType]
  );
  return (
    <div className="rule-builder">
      <Transformer
        isDark={isDark}
        addBtnText={addBtnText}
        noResult={noResult}
        hideCollapse={hideCollapse}
        disabled={disabled}
        onHandleScriptModal={onHandleScriptModal}
        ruleTypes={ruleTypes}
        title={title}
        errors={errors}
        createTransform={createTransform}
        onRemoveTransform={onRemoveTransform}
        onValueChangeHandler={onValueChangeHandler}
        onDropTransform={onDropTransform}
        value={root}
        transformType={transformType}
        titleConfirmRemove={titleConfirmRemove}
        isDynamicConditionType={isDynamicConditionType}
      />
    </div>
  );
});

export default TransformBuilder;
